import { formatCurrency } from './formatting';

export type PaymentsDueStatus = 'near' | 'due_today' | 'overdue';

export interface PaymentsDueQuery {
  searchName: string;
  paymentGateway: string[];
  dueStart: string;
  dueEnd: string;
  overdueMinDays: '' | '7' | '15' | '30';
  page: number;
  pageSize: number;
}

export type PaymentsDueQueryResult =
  | { ok: true; query: PaymentsDueQuery }
  | { ok: false; message: string };

export const PAYMENTS_DUE_MONTHLY_CYCLE_DAYS = 30;
export const PAYMENTS_DUE_SINGLE_PAYMENT_CYCLE_DAYS = 365;

export interface PaymentsDueRow {
  studentId: number;
  studentName: string;
  studentEmail: string;
  planName: string | null;
  lastPaidAt: string | null;
  paymentSystem: string | null;
  value: number | null;
  currency: string | null;
  daysSincePaid: number;
  dueDate: string | null;
  cycleDays: number | null;
  billingPeriodMonths: number | null;
  paymentHistoryId: number | null;
  isSinglePayment: boolean;
}

export interface PaymentsDueDashboard {
  page: number;
  pageSize: number;
  totalCount: number;
  students: PaymentsDueRow[];
}

const ALLOWED_OVERDUE_MIN_DAYS = new Set(['', '7', '15', '30']);

export function resolvePaymentsDueQuery(
  searchName: string,
  paymentGateway: readonly string[],
  dueStart: string,
  dueEnd: string,
  overdueMinDays: string,
  page: number,
  pageSize: number,
): PaymentsDueQueryResult {
  const start = dueStart.trim();
  const end = dueEnd.trim();
  const hasStart = Boolean(start);
  const hasEnd = Boolean(end);
  const overdue = overdueMinDays.trim();

  if (!ALLOWED_OVERDUE_MIN_DAYS.has(overdue)) {
    return { ok: false, message: 'Dias vencidos deve ser 7, 15, 30 ou Todos.' };
  }

  if (hasStart !== hasEnd) {
    return { ok: false, message: 'Informe a data inicial e a data final do vencimento.' };
  }

  if (hasStart && hasEnd) {
    if (!isIsoDate(start) || !isIsoDate(end)) {
      return { ok: false, message: 'Informe um período de vencimento válido.' };
    }

    if (start > end) {
      return { ok: false, message: 'A data inicial do vencimento não pode ser maior que a final.' };
    }
  }

  return {
    ok: true,
    query: {
      searchName,
      paymentGateway: [...paymentGateway],
      dueStart: start,
      dueEnd: end,
      overdueMinDays: overdue as PaymentsDueQuery['overdueMinDays'],
      page,
      pageSize,
    },
  };
}

export function paymentsDueCycleDays(
  isSinglePayment = false,
  cycleDays?: number | null,
): number {
  if (cycleDays != null && cycleDays > 0) {
    return cycleDays;
  }

  return isSinglePayment
    ? PAYMENTS_DUE_SINGLE_PAYMENT_CYCLE_DAYS
    : PAYMENTS_DUE_MONTHLY_CYCLE_DAYS;
}

export function resolvePaymentsDueStatus(
  daysSincePaid: number,
  isSinglePayment = false,
  cycleDays?: number | null,
): PaymentsDueStatus {
  const resolvedCycleDays = paymentsDueCycleDays(isSinglePayment, cycleDays);

  if (daysSincePaid > resolvedCycleDays) {
    return 'overdue';
  }

  if (daysSincePaid === resolvedCycleDays) {
    return 'due_today';
  }

  return 'near';
}

export function paymentsDueDaysOverdue(
  daysSincePaid: number,
  isSinglePayment = false,
  cycleDays?: number | null,
): number {
  return Math.max(0, daysSincePaid - paymentsDueCycleDays(isSinglePayment, cycleDays));
}

export function paymentsDueStatusLabel(
  status: PaymentsDueStatus,
  daysSincePaid: number,
  isSinglePayment = false,
  cycleDays?: number | null,
): string {
  if (status === 'due_today') {
    return 'Vence hoje';
  }

  if (status === 'overdue') {
    const daysOverdue = paymentsDueDaysOverdue(daysSincePaid, isSinglePayment, cycleDays);
    return `Vencido · ${daysOverdue} ${daysOverdue === 1 ? 'dia' : 'dias'}`;
  }

  return `Perto de vencer · pago há ${daysSincePaid} ${daysSincePaid === 1 ? 'dia' : 'dias'}`;
}

export function paymentsDueStatusBadgeVariant(
  status: PaymentsDueStatus,
): 'info' | 'warning' | 'danger' {
  if (status === 'near') {
    return 'warning';
  }

  if (status === 'due_today') {
    return 'info';
  }

  return 'danger';
}

export function formatPaymentDueAmount(value: number | null, currency: string | null): string {
  if (value == null) {
    return '—';
  }

  if (currency === 'BRL' || currency === 'USD') {
    return formatCurrency(value, currency);
  }

  const formatted = value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${formatted}` : formatted;
}

export function parsePaymentsDueDashboard(value: unknown): PaymentsDueDashboard | null {
  if (!isRecord(value)) {
    return null;
  }

  const students = readStudents(value.students);
  const totalCount = readNonNegativeInteger(value.totalCount);
  const page = readPositiveInteger(value.page) ?? 1;
  const pageSize = readPositiveInteger(value.pageSize) ?? 20;
  if (!students || totalCount == null) {
    return null;
  }

  return {
    page,
    pageSize,
    totalCount,
    students,
  };
}

function readStudents(value: unknown): PaymentsDueRow[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const students: PaymentsDueRow[] = [];
  for (const item of value) {
    const row = readStudent(item);
    if (!row) {
      return null;
    }
    students.push(row);
  }

  return students;
}

function readStudent(value: unknown): PaymentsDueRow | null {
  if (!isRecord(value)) {
    return null;
  }

  const studentId = readPositiveInteger(value.studentId);
  const studentName = readOptionalText(value.studentName);
  const studentEmail = value.studentEmail == null ? '' : readOptionalText(value.studentEmail);
  const planName = value.planName == null ? null : readOptionalText(value.planName);
  const lastPaidAt = value.lastPaidAt == null ? null : readTimestamp(value.lastPaidAt);
  const paymentSystem = value.paymentSystem == null ? null : readOptionalText(value.paymentSystem);
  const amount = value.value == null ? null : readNonNegativeAmount(value.value);
  const currency = value.currency == null ? null : readOptionalText(value.currency);
  const daysSincePaid = readNonNegativeInteger(value.daysSincePaid);
  const dueDate = value.dueDate == null ? null : readIsoDate(value.dueDate);
  const cycleDays = value.cycleDays == null ? null : readPositiveInteger(value.cycleDays);
  const billingPeriodMonths = value.billingPeriodMonths == null
    ? null
    : readPositiveInteger(value.billingPeriodMonths);
  const paymentHistoryId = value.paymentHistoryId == null
    ? null
    : readPositiveInteger(value.paymentHistoryId);
  const isSinglePayment = value.isSinglePayment == null
    ? false
    : readBoolean(value.isSinglePayment);

  if (
    studentId == null
    || studentName == null
    || studentEmail == null
    || (value.planName != null && planName == null)
    || (value.lastPaidAt != null && !lastPaidAt)
    || (value.paymentSystem != null && paymentSystem == null)
    || (value.value != null && amount == null)
    || (value.currency != null && currency == null)
    || daysSincePaid == null
    || (value.dueDate != null && !dueDate)
    || (value.cycleDays != null && cycleDays == null)
    || (value.billingPeriodMonths != null && billingPeriodMonths == null)
    || (value.paymentHistoryId != null && paymentHistoryId == null)
    || isSinglePayment == null
  ) {
    return null;
  }

  return {
    studentId,
    studentName,
    studentEmail,
    planName,
    lastPaidAt,
    paymentSystem,
    value: amount,
    currency,
    daysSincePaid,
    dueDate,
    cycleDays,
    billingPeriodMonths,
    paymentHistoryId,
    isSinglePayment,
  };
}

function readBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }

  return null;
}

function isIsoDate(value: string): boolean {
  return readIsoDate(value) != null;
}

function readIsoDate(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return null;
  }

  return value;
}

function readTimestamp(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return value;
}

function readPositiveInteger(value: unknown): number | null {
  const parsed = readNumber(value);
  if (parsed == null || !Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function readNonNegativeInteger(value: unknown): number | null {
  const parsed = readNumber(value);
  if (parsed == null || !Number.isInteger(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
}

function readNonNegativeAmount(value: unknown): number | null {
  const parsed = readNumber(value);
  if (parsed == null || parsed < 0) {
    return null;
  }

  return parsed;
}

function readNumber(value: unknown): number | null {
  const parsed = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number(value)
      : Number.NaN;

  if (!Number.isFinite(parsed) || parsed > Number.MAX_SAFE_INTEGER) {
    return null;
  }

  return parsed;
}

function readOptionalText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed === '' ? '' : trimmed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
